/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useMemo, useRef, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import moment from 'moment-timezone';
import { BoxShadow } from '../../../../shared/GlobalStyles';
import DraggableContent from './DraggableContent';
import { setExpandedItem, removeExpandedItem, setSessionBreakCollapsed } from '../../ducks/calendarActions';
import OverlapIcon from '../../../../shared/components/OverlapIcon/Overlapicon';

const DraggableDiv = styled('div')`
  border-radius: ${(props) => (props.isDragging || props.isHovered ? '4px' : 'none')};
  border: ${(props) => (props.isDragging || (props.isHovered && !props.disableBlueBorder) ? `1px dashed ${props.theme.colors.hover}` : props.theme.colors.surface)};
  box-shadow: ${(props) => (props.isDragging ? `${BoxShadow}` : 'none')};
  background: ${(props) => props.customBackground || props.theme.colors.surface};
  transition: none;
  margin-top: ${(props) => (props.addMarginTop && !props.isSessionBreak
    ? '3px'
    : props.addMarginTop && props.isSessionBreak
      ? '8px' : null)};
  margin-bottom: ${(props) => props.addMarginBottom && '3px'};
  max-height: auto;
  position: relative;
`;

const MultiItemBadge = styled('div')`
position: absolute;
right: -10px;
top: -19px;
height: 38px;
width: 38px;
border-radius: 50%;
display: flex;
align-items: center;
justify-content: center;
border: 1px solid ${({ theme }) => theme.colors.borderLight};
background: ${({ theme }) => theme.colors.surface};
opacity: 1;
/* Shadow/Theme/20 */
box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);

`;

const RenderCloneDraggableDivWrapper = ({
  item, index, provided, snapshot, date, dayNumber,
}) => {
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  const { colors } = useTheme();
  const [localAdditionalInfo, setLocalAdditionalInfo] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedItems = useSelector((state) => state.calendar.data.selectedItems);
  const selectedItem = useSelector((state) => state.calendar.data.selectedItems[item.assignedId]);
  const expandedItem = useSelector((state) => state.calendar.data.expandedItems[item.assignedId]);
  const EMPTY_EXERCISES = useRef([]).current;
  const formattedDate = useMemo(
    () => (date ? moment(new Date(date)).format('YYYY-MM-DD') : null),
    [date],
  );
  const nonDateItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems[
      dayNumber
    ]?.exercises || EMPTY_EXERCISES,
  );
  const dateItems = useSelector(
    (state) => (formattedDate
      ? state.calendar.data.calendarItems[formattedDate]?.exercises || EMPTY_EXERCISES
      : EMPTY_EXERCISES),
  );
  const calendarItems = programType === 'free-form-program' ? nonDateItems : dateItems;

  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);
  // eslint-disable-next-line no-unused-vars
  const [hoveredId, setHoveredId] = useState(null);

  const doesGroupingLetterAboveItemMatchContent = !!item.groupingLetter
    && item.groupingLetter === calendarItems[index - 1]?.groupingLetter;

  const doesGroupingLetterBelowItemMatchContent = !!item.groupingLetter
    && item.groupingLetter === calendarItems[index + 1]?.groupingLetter;

  const isCurrentItemBelowASessionBreak = (currentItemId) => {
    const currentIndex = calendarItems.findIndex(
      (calendarItem) => calendarItem.assignedId === currentItemId,
    );
    if (currentIndex === -1) return false;
    if (calendarItems[currentIndex]?.type === 'session_break') return false;
    return calendarItems.slice(0, currentIndex).some((i) => i.type === 'session_break');
  };

  const getItemsUnderSessionBreak = (currentItem) => {
    if (!currentItem || currentItem.type !== 'session_break') return {};
    const currentIndex = calendarItems.findIndex(
      (c) => c.assignedId === currentItem.assignedId,
    );
    if (currentIndex === -1) return {};
    const result = {};
    let reachedNextBreak = false;
    // eslint-disable-next-line array-callback-return
    calendarItems.map((calItem, i) => {
      if (i <= currentIndex || reachedNextBreak) return;
      if (calItem.type === 'session_break') {
        reachedNextBreak = true;
        return;
      }
      result[calItem.assignedId] = calItem;
    });
    return result;
  };

  const sessionBreakAbove = useMemo(() => {
    if (!item || item.type === 'session_break') return null;
    let sb = null;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < calendarItems.length; i++) {
      if (calendarItems[i]?.assignedId === item?.assignedId) break;
      if (calendarItems[i]?.type === 'session_break') sb = calendarItems[i];
    }
    return sb;
  }, [calendarItems, item]);

  const isSessionBreakAboveExpanded = useSelector(
    (state) => (sessionBreakAbove
      ? !!state.calendar.data.expandedItems[sessionBreakAbove.assignedId]
      : false),
  );

  const shouldCollapse = useMemo(() => {
    if (
      draggedItem?.type === 'session_break'
      && getItemsUnderSessionBreak(draggedItem)?.[item.assignedId]
    ) {
      return false;
    }

    if (
      sessionBreakAbove
      && !sessionBreakAbove.preSave
      && getItemsUnderSessionBreak(sessionBreakAbove)?.[item.assignedId]
      && !isSessionBreakAboveExpanded
    ) {
      return true;
    }

    return false;
  }, [item,
    draggedItem,
    sessionBreakAbove,
    isSessionBreakAboveExpanded, getItemsUnderSessionBreak]);

  const handleClick = () => {
    if (expandedItem) {
      if (item?.type === 'session_break') dispatch(setSessionBreakCollapsed(item));
      else dispatch(removeExpandedItem(item));
    } else dispatch(setExpandedItem(item));
  };

  const typeDoesNotExpand = (typeItem) => ['warm_up', 'W', 'health', 'H', 'note', 'N', 'cooldown', 'CD'].includes(typeItem.type);

  const handleIsCheckBoxForTypeVisible = (checkBoxItem) => hoveredId === `${checkBoxItem.assignedId}-programmedCalendarItems` || selectedItem;

  const itemIdStr = `${item.assignedId}-programmedCalendarItems`;
  const itemHoveredOverId = snapshot?.combineTargetFor?.split('-programmedCalendarItems')[0];
  const itemHoveringOver = calendarItems?.find(
    (calendarItem) => calendarItem?.assignedId?.toString() === itemHoveredOverId,
  );

  const shouldHighlight = (
    (hoveredId === itemIdStr && !expandedItem && !item.preSave)
    || (hoveredId === itemIdStr && typeDoesNotExpand(item))
    || selectedItem
  );

  const selectedCountForDay = Object.keys(selectedItems || {}).filter(
    (selectedId) => calendarItems.some(
      (calItem) => String(calItem.assignedId) === selectedId,
    ),
  ).length;

  const getMultiSelectCount = () => {
    const isSessionBreakDrag = draggedItem?.type === 'session_break';
    const itemsUnderBreak = getItemsUnderSessionBreak(draggedItem) || {};

    const sessionBreakAssignedIds = [
      String(draggedItem?.assignedId),
      ...Object.keys(itemsUnderBreak || {}),
    ];

    const selectedAssignedIds = Object.keys(selectedItems || {});

    const onlySessionBreakItemsSelected = selectedAssignedIds.length > 0
    && selectedAssignedIds.length === sessionBreakAssignedIds.length
    && selectedAssignedIds.every((id) => sessionBreakAssignedIds.includes(id));

    if (
      isSessionBreakDrag
    && Object.keys(itemsUnderBreak).length > 0
    && (
      selectedItem?.assignedId !== draggedItem?.assignedId
      || onlySessionBreakItemsSelected
    )
    ) {
      return Object.keys(itemsUnderBreak).length;
    }

    return selectedCountForDay;
  };

  /** the clone floats over other content, so it needs an opaque fill -
   * surfaceHover is a translucent overlay and would let the list show through */
  const computedBackground = shouldHighlight
    ? colors.surfaceHoverStrong
    : (item.type === 'session_break' || isCurrentItemBelowASessionBreak(item?.assignedId))
      ? colors.surfaceSecondary
      : colors.surface;

  const computedStyle = useMemo(() => ({
    ...(provided.draggableProps.style),
    opacity: shouldCollapse ? 0 : 1,
  }), [provided.draggableProps.style, shouldCollapse]);

  const setRefs = useCallback((node) => {
    contentRef.current = node;
    provided.innerRef(node);
  }, [provided]);

  return (
    <DraggableDiv
      ref={setRefs}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      isDragging={snapshot.isDragging}
      isHovered={snapshot.combineTargetFor != null}
      style={computedStyle}
      customBackground={computedBackground}
      disableBlueBorder={item.type === 'session_break' || itemHoveringOver?.type === 'session_break' || item?.type === 'questionnaire' || itemHoveringOver?.type === 'questionnaire'}
      addMarginTop={!doesGroupingLetterAboveItemMatchContent
        && index !== 0 && !isCurrentItemBelowASessionBreak(item?.assignedId)}
      isSessionBreak={item.type === 'session_break'}
      addMarginBottom={!doesGroupingLetterBelowItemMatchContent && item.type !== 'session_break' && !isCurrentItemBelowASessionBreak(item?.assignedId)}
    >
      {((selectedItem && (draggedItem?.assignedId === selectedItem?.assignedId) && selectedCountForDay > 1) || (draggedItem?.type === 'session_break' && Object.keys(getItemsUnderSessionBreak(draggedItem) || {}).length >= 1)) && (
      <MultiItemBadge>
        <OverlapIcon
          count={getMultiSelectCount()}
          backgroundColor='white'
        />
      </MultiItemBadge>
      )}
      {!shouldCollapse && (
        <DraggableContent
          localDescription={localDescription}
          setLocalDescription={setLocalDescription}
          localAdditionalInfo={localAdditionalInfo}
          setLocalAdditionalInfo={setLocalAdditionalInfo}
          date={date}
          dayNumber={dayNumber}
          isWeekView={viewType === 'weekView'}
          isHovered={snapshot.combineTargetFor != null}
          itemHoveringOverCurrentItem={itemHoveringOver}
          itemIsHoveredByMouse={hoveredId === itemIdStr}
          isDragging={snapshot.isDragging}
          content={item}
          index={index}
          shouldShowQuickActions={shouldHighlight}
          isExpanded={!!expandedItem}
          setIsExpanded={() => handleClick(item.assignedId)}
          checkboxVisible={
            typeDoesNotExpand(item)
              ? handleIsCheckBoxForTypeVisible(item)
              : hoveredId === itemIdStr || selectedItem || !!expandedItem || item.preSave
          }
        />
      )}
    </DraggableDiv>
  );
};

export default RenderCloneDraggableDivWrapper;
